'use client';

import { useEffect, useRef, useState, useTransition } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Bug,
  ChevronDown,
  Inbox,
  LifeBuoy,
  Lightbulb,
  Loader2,
  MessageSquareText,
  Paperclip,
  User,
  Users,
  X,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useSupabase } from '@kit/supabase/hooks/use-supabase';
import { useUser } from '@kit/supabase/hooks/use-user';
import { Badge } from '@kit/ui/badge';
import { Button } from '@kit/ui/button';
import { toast } from '@kit/ui/sonner';
import { Spinner } from '@kit/ui/spinner';
import { Textarea } from '@kit/ui/textarea';
import { cn } from '@kit/ui/utils';

import { replyToBugReportAction } from '../../_lib/server/bug-report-actions';
import { AttachmentThumbnail } from './attachment-thumbnail';
import { compressImageIfNeeded } from './compress-image';
import {
  BUG_REPORT_ACCEPTED_MIME,
  type BugReportStatus,
  type BugReportType,
  getAttachmentExtension,
  REPORT_STATUS_LABELS,
  REPORT_TYPE_LABELS,
} from './report-labels';

// Reply attachments reuse the bug_reports bucket and the submission dialog's
// shared accepted-MIME list (images, MP4, PDF, Word docs), with a 5-file cap.
const REPLY_MAX_FILES = 5;
const REPLY_ACCEPTED_MIME: readonly string[] = BUG_REPORT_ACCEPTED_MIME;
const REPLY_ATTACHMENT_URL_TTL_SECONDS = 60 * 60;

interface MyReport {
  id: string;
  title: string;
  report_type: string;
  severity: string;
  status: string;
  admin_response: string | null;
  created_at: string;
  // Nullable: bug_reports.reporter_user_id is ON DELETE SET NULL.
  reporter_user_id: string | null;
}

interface ReportComment {
  id: string;
  report_id: string;
  actor_user_id: string | null;
  new_value: string | null;
  attachments: string[];
  created_at: string;
}

const REPORT_SELECT =
  'id, title, report_type, severity, status, admin_response, created_at, reporter_user_id';

const STATUS_BADGE_CLASSES: Record<BugReportStatus, string> = {
  new: 'bg-muted text-muted-foreground',
  in_progress: 'bg-blue-500/15 text-blue-700 dark:text-blue-400',
  // indigo maps to no semantic status role, so it stays a raw class — design-system.md deliberately excludes it from status tokens.
  pending_release: 'bg-indigo-500/15 text-indigo-700 dark:text-indigo-400',
  reopened: 'bg-orange-500/15 text-orange-700 dark:text-orange-400',
  planned: 'bg-purple-500/15 text-purple-700 dark:text-purple-400',
  future: 'bg-teal-500/15 text-teal-700 dark:text-teal-400',
  done: 'bg-green-500/15 text-green-700 dark:text-green-400',
  declined: 'bg-red-500/10 text-red-700 dark:text-red-400',
};

export function MyReportsList({
  accountId,
  highlightReportId,
}: {
  accountId: string;
  highlightReportId?: string | null;
}) {
  const supabase = useSupabase();
  const { data: user } = useUser();
  const { t } = useTranslation('common');
  const listRef = useRef<HTMLDivElement | null>(null);

  const [expandedId, setExpandedId] = useState<string | null>(
    highlightReportId ?? null,
  );

  // My own reports. Behaviour unchanged from before the "From my team" split —
  // a dedicated query keeps the 50-row cap scoped to MY reports so teammates'
  // activity can never crowd my own out of view.
  const { data: myReports, isLoading } = useQuery({
    queryKey: ['my-bug-reports', accountId, user?.id],
    enabled: Boolean(user?.id),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bug_reports')
        .select(REPORT_SELECT)
        .eq('account_id', accountId)
        .eq('reporter_user_id', user!.id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      return data as MyReport[];
    },
  });

  // Teammates' reports for the same business. bug_reports_read already lets any
  // team member read every report on their tenant, so this is not a wider data
  // surface — it just surfaces what RLS already permits, in its own section.
  const { data: teamReports } = useQuery({
    queryKey: ['team-bug-reports', accountId, user?.id],
    enabled: Boolean(user?.id),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bug_reports')
        .select(REPORT_SELECT)
        .eq('account_id', accountId)
        .neq('reporter_user_id', user!.id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      return data as MyReport[];
    },
  });

  // Reporter display names for the "From my team" section. accounts_read allows
  // a team member to read a teammate's personal-account name.
  const teamReporterIds = [
    ...new Set(
      (teamReports ?? [])
        .map((report) => report.reporter_user_id)
        .filter((id): id is string => Boolean(id)),
    ),
  ];
  const { data: reporterNameById } = useQuery({
    queryKey: ['bug-report-reporters', accountId, teamReporterIds.join(',')],
    enabled: teamReporterIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('accounts')
        .select('id, name')
        .in('id', teamReporterIds);

      if (error) throw error;

      const map = new Map<string, string>();
      for (const row of data ?? []) {
        if (row.name) map.set(row.id, row.name);
      }
      return map;
    },
  });

  const allReportIds = [...(myReports ?? []), ...(teamReports ?? [])].map(
    (report) => report.id,
  );

  // Conversation messages for every listed report in one query. RLS now returns
  // 'comment' events on any report of the caller's tenant (mine + teammates').
  const { data: comments } = useQuery({
    queryKey: ['bug-report-comments', accountId, user?.id, allReportIds.join(',')],
    enabled: allReportIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bug_report_events')
        .select('id, report_id, actor_user_id, new_value, attachments, created_at')
        .in('report_id', allReportIds)
        .eq('event_type', 'comment')
        .order('created_at', { ascending: true });

      if (error) throw error;

      // attachments is jsonb — coerce to a clean string[] for the UI.
      return (data ?? []).map((row) => ({
        ...row,
        attachments: Array.isArray(row.attachments)
          ? row.attachments.filter(
              (path): path is string => typeof path === 'string',
            )
          : [],
      })) as ReportComment[];
    },
  });

  // Bring the deep-linked report into view once the list has rendered.
  useEffect(() => {
    if (!highlightReportId || (!myReports && !teamReports)) return;

    listRef.current
      ?.querySelector(`[data-report-id="${highlightReportId}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [highlightReportId, myReports, teamReports]);

  if (isLoading || !user) {
    return (
      <div className="flex justify-center py-10">
        <Spinner />
      </div>
    );
  }

  const mine = myReports ?? [];
  const team = teamReports ?? [];

  if (mine.length === 0 && team.length === 0) {
    return (
      <div className="text-muted-foreground flex flex-col items-center gap-2 py-10 text-center">
        <Inbox className="h-8 w-8" aria-hidden="true" />
        <p className="text-sm">
          {t('bugReport.myReports.empty', {
            defaultValue:
              "You haven't submitted anything yet. Bugs and feature requests you send will show up here with their status.",
          })}
        </p>
      </div>
    );
  }

  const commentsByReport = new Map<string, ReportComment[]>();

  for (const comment of comments ?? []) {
    const list = commentsByReport.get(comment.report_id) ?? [];
    list.push(comment);
    commentsByReport.set(comment.report_id, list);
  }

  const renderList = (reports: MyReport[], canReply: boolean) => (
    <ul className="space-y-2">
      {reports.map((report) => (
        <ReportItem
          key={report.id}
          report={report}
          comments={commentsByReport.get(report.id) ?? []}
          isExpanded={expandedId === report.id}
          onToggle={() =>
            setExpandedId(expandedId === report.id ? null : report.id)
          }
          canReply={canReply}
          reporterName={
            canReply
              ? null
              : (report.reporter_user_id
                  ? reporterNameById?.get(report.reporter_user_id)
                  : null) ?? null
          }
          currentUserId={user.id}
          accountId={accountId}
        />
      ))}
    </ul>
  );

  return (
    <div
      ref={listRef}
      className="max-h-[420px] space-y-4 overflow-y-auto pr-1"
    >
      <section className="space-y-2">
        <SectionHeader
          icon={<User className="h-3.5 w-3.5" aria-hidden="true" />}
          title={t('bugReport.myReports.mineHeading', {
            defaultValue: 'My reports',
          })}
          count={mine.length}
        />

        {mine.length > 0 ? (
          renderList(mine, true)
        ) : (
          <p className="text-muted-foreground py-2 text-xs">
            {t('bugReport.myReports.mineEmpty', {
              defaultValue: "You haven't submitted anything yet.",
            })}
          </p>
        )}
      </section>

      {team.length > 0 ? (
        // A full-bleed divider + its own header makes it unmistakable that a new,
        // read-only section begins here — everything below is a teammate's report,
        // not the viewer's own.
        <section className="border-border space-y-2 border-t pt-4">
          <SectionHeader
            icon={<Users className="h-3.5 w-3.5" aria-hidden="true" />}
            title={t('bugReport.myReports.teamHeading', {
              defaultValue: 'From my team',
            })}
            count={team.length}
            subtitle={t('bugReport.myReports.teamSubheading', {
              defaultValue: 'Reports from your teammates — view only',
            })}
          />

          {renderList(team, false)}
        </section>
      ) : null}
    </div>
  );
}

/**
 * Section label for the "My reports" / "From my team" split. An icon + count
 * badge (and an optional subtitle) give each section a scannable identity so the
 * two groups don't blur together. RTL-safe: `ps-*` mirrors and the flex row
 * flips automatically.
 */
function SectionHeader({
  icon,
  title,
  count,
  subtitle,
}: {
  icon: React.ReactNode;
  title: string;
  count: number;
  subtitle?: string;
}) {
  return (
    <div className="space-y-1">
      <div className="text-foreground flex items-center gap-2">
        <span className="text-muted-foreground" aria-hidden="true">
          {icon}
        </span>
        <h3 className="text-xs font-semibold tracking-wide uppercase">
          {title}
        </h3>
        {count > 0 ? (
          // Neutral count pill: bg-muted is a plain gray in every tenant theme
          // (never brand-tinted), so it stays simple and legible in light + dark.
          <span className="bg-muted text-foreground inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-medium tabular-nums">
            {count}
          </span>
        ) : null}
      </div>
      {subtitle ? (
        <p className="text-muted-foreground ps-[1.375rem] text-[11px]">
          {subtitle}
        </p>
      ) : null}
    </div>
  );
}

/** One report card — used in both the "My reports" and "From my team" sections. */
function ReportItem({
  report,
  comments,
  isExpanded,
  onToggle,
  canReply,
  reporterName,
  currentUserId,
  accountId,
}: {
  report: MyReport;
  comments: ReportComment[];
  isExpanded: boolean;
  onToggle: () => void;
  canReply: boolean;
  reporterName?: string | null;
  currentUserId: string;
  accountId: string;
}) {
  const { t } = useTranslation('common');

  const status = (
    report.status in STATUS_BADGE_CLASSES ? report.status : 'new'
  ) as BugReportStatus;

  const isFeature = report.report_type === 'feature_request';
  const isSupport = report.report_type === 'support_request';
  const type = (isFeature
    ? 'feature_request'
    : isSupport
      ? 'support_request'
      : 'bug') as BugReportType;

  return (
    <li
      data-report-id={report.id}
      className="border-border/60 hover:border-border rounded-lg border px-3.5 py-3 transition-colors"
    >
      <div className="flex items-start justify-between gap-2">
        <span className="flex min-w-0 items-center gap-2">
          {isFeature ? (
            <Lightbulb
              className="text-muted-foreground h-4 w-4 shrink-0"
              aria-hidden="true"
            />
          ) : isSupport ? (
            <LifeBuoy
              className="text-muted-foreground h-4 w-4 shrink-0"
              aria-hidden="true"
            />
          ) : (
            <Bug
              className="text-muted-foreground h-4 w-4 shrink-0"
              aria-hidden="true"
            />
          )}
          <span className="truncate text-sm font-medium">{report.title}</span>
        </span>
        <Badge
          variant="outline"
          className={cn('shrink-0 border-none', STATUS_BADGE_CLASSES[status])}
        >
          {t(`bugReport.status.${status}`, {
            defaultValue: REPORT_STATUS_LABELS[status],
          })}
        </Badge>
      </div>

      <div className="text-muted-foreground mt-1 text-xs">
        {t(
          type === 'bug'
            ? 'bugReport.type.bug.label'
            : type === 'support_request'
              ? 'bugReport.type.support.label'
              : 'bugReport.type.feature.label',
          { defaultValue: REPORT_TYPE_LABELS[type] },
        )}{' '}
        · {new Date(report.created_at).toLocaleDateString()}
        {reporterName ? (
          <>
            {' '}
            ·{' '}
            {t('bugReport.myReports.reportedBy', {
              defaultValue: 'Reported by {{name}}',
              name: reporterName,
            })}
          </>
        ) : null}
      </div>

      {report.admin_response ? (
        <div className="bg-muted/40 mt-2 flex items-start gap-2 rounded-lg px-3 py-2 text-xs">
          <MessageSquareText
            className="text-muted-foreground mt-0.5 h-3.5 w-3.5 shrink-0"
            aria-hidden="true"
          />
          <span className="min-w-0 break-words whitespace-pre-wrap">
            {report.admin_response}
          </span>
        </div>
      ) : null}

      <button
        type="button"
        className="text-muted-foreground hover:text-foreground mt-2 flex items-center gap-1.5 text-xs font-medium"
        onClick={onToggle}
        aria-expanded={isExpanded}
      >
        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 transition-transform',
            isExpanded && 'rotate-180',
          )}
          aria-hidden="true"
        />
        {comments.length > 0
          ? t('bugReport.thread.toggleWithCount', {
              defaultValue: 'Conversation ({{count}})',
              count: comments.length,
            })
          : t('bugReport.thread.toggle', {
              defaultValue: 'Conversation',
            })}
      </button>

      {isExpanded ? (
        <ReportThread
          accountId={accountId}
          reportId={report.id}
          comments={comments}
          currentUserId={currentUserId}
          canReply={canReply}
          reporterUserId={report.reporter_user_id}
          reporterName={reporterName}
        />
      ) : null}
    </li>
  );
}

/**
 * The back-and-forth thread with the AskAI team for one report. Messages are
 * 'comment' rows in bug_report_events; replies go through
 * replyToBugReportAction (service-role insert, gated on reporter ownership).
 *
 * canReply is false for teammates' reports in the "From my team" section: the
 * thread is read-only there (only the original reporter may reply, matching the
 * reporter_user_id gate in replyToBugReportAction — visibility never grants it).
 */
function ReportThread({
  accountId,
  reportId,
  comments,
  currentUserId,
  canReply,
  reporterUserId,
  reporterName,
}: {
  accountId: string;
  reportId: string;
  comments: ReportComment[];
  currentUserId: string;
  canReply: boolean;
  // The report's own reporter — used to attribute their messages. In the "From
  // my team" section the reporter is a teammate (not the viewer), so their
  // comments must show their name, not "AskAI Support".
  reporterUserId: string | null;
  reporterName?: string | null;
}) {
  const { t } = useTranslation('common');
  const supabase = useSupabase();
  const queryClient = useQueryClient();
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [isSending, startTransition] = useTransition();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Sign every attachment path referenced by this thread's comments so the
  // reporter (a team member of this tenant → bug_reports_read RLS) can view
  // them. Batched into a single createSignedUrls call.
  const attachmentPaths = comments.flatMap((comment) => comment.attachments);
  const { data: signedUrlByPath } = useQuery({
    queryKey: ['bug-report-comment-attachments', reportId, attachmentPaths.join(',')],
    enabled: attachmentPaths.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase.storage
        .from('bug_reports')
        .createSignedUrls(attachmentPaths, REPLY_ATTACHMENT_URL_TTL_SECONDS);

      if (error) throw error;

      const map = new Map<string, string>();
      for (const entry of data ?? []) {
        if (entry.path && entry.signedUrl) {
          map.set(entry.path, entry.signedUrl);
        }
      }
      return map;
    },
  });

  const addFiles = (list: FileList | null) => {
    if (!list) return;

    const selected = Array.from(list);
    const accepted = selected.filter((file) =>
      REPLY_ACCEPTED_MIME.includes(file.type),
    );

    // Explain missing chips instead of silently dropping files: unsupported
    // MIME types are filtered out and anything past the cap is sliced. Computed
    // from the `files` closure (not inside the setState updater) so a StrictMode
    // double-invoke can't fire the toast twice.
    const rejected = selected.length - accepted.length;
    const combined = [...files, ...accepted];

    if (rejected > 0) {
      toast.error(
        t('bugReport.thread.attachmentTypeRejected', {
          defaultValue: 'Some files were skipped — unsupported type',
        }),
      );
    }

    if (combined.length > REPLY_MAX_FILES) {
      toast.error(
        t('bugReport.thread.attachmentLimit', {
          max: REPLY_MAX_FILES,
          defaultValue: 'You can attach up to {{max}} files',
        }),
      );
    }

    setFiles(combined.slice(0, REPLY_MAX_FILES));

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const onSend = () => {
    const trimmed = message.trim();
    if (!trimmed && files.length === 0) return;

    // Hoisted out of the try so the catch can clean up whatever already landed.
    const uploadedPaths: string[] = [];

    startTransition(async () => {
      try {
        // Upload attachments to THIS report's own folder first; the server
        // action re-checks every path is bound to {account_id}/{report_id}/.
        // Sequential (not Promise.all) on purpose: a failed upload attributes
        // cleanly to one file, and uploadedPaths then holds exactly what landed
        // so the catch can remove precisely those objects. File count is capped
        // at REPLY_MAX_FILES, so there is no meaningful latency win from parallel.
        for (const raw of files) {
          // compressImageIfNeeded is a no-op for non-image types (video/pdf/doc
          // pass through unchanged), so they upload as-is. Derive the extension
          // from the (possibly compressed) file so a .docx/.pdf keeps its real
          // suffix instead of being mislabeled .jpg.
          const file = await compressImageIfNeeded(raw);
          const extension = getAttachmentExtension(file);
          const path = `${accountId}/${reportId}/${crypto.randomUUID()}${extension}`;

          const { error } = await supabase.storage
            .from('bug_reports')
            .upload(path, file, { upsert: false, contentType: file.type });

          if (error) throw error;
          uploadedPaths.push(path);
        }

        const result = await replyToBugReportAction({
          reportId,
          message: trimmed,
          attachments: uploadedPaths,
        });

        if (!result?.success) {
          throw new Error('Failed to send reply');
        }

        setMessage('');
        setFiles([]);
        void queryClient.invalidateQueries({
          queryKey: ['bug-report-comments', accountId],
        });
      } catch {
        // Best-effort cleanup: drop any objects uploaded before the failure so a
        // partial upload or rejected action doesn't leave orphans in the bucket.
        // Fire-and-forget — a cleanup error must not mask the original one.
        if (uploadedPaths.length > 0) {
          void supabase.storage.from('bug_reports').remove(uploadedPaths);
        }

        toast.error(
          t('bugReport.thread.sendFailed', {
            defaultValue: 'Failed to send your reply. Please try again.',
          }),
        );
      }
    });
  };

  return (
    <div className="mt-2 space-y-2 border-t pt-2">
      {comments.length > 0 ? (
        <ul className="space-y-1.5">
          {comments.map((comment) => {
            const isMine = comment.actor_user_id === currentUserId;
            // Attribute the reporter's own messages to them; everything else on
            // a 'comment' thread is the AskAI team. In "My reports" the reporter
            // is the viewer, so isMine wins first and this collapses to You/AskAI.
            const isReporter =
              !isMine &&
              comment.actor_user_id != null &&
              comment.actor_user_id === reporterUserId;
            const authorLabel = isMine
              ? t('bugReport.thread.you', { defaultValue: 'You' })
              : isReporter
                ? (reporterName ??
                  t('bugReport.thread.teammate', { defaultValue: 'Teammate' }))
                : t('bugReport.thread.supportTeam', {
                    defaultValue: 'AskAI Support',
                  });

            return (
              <li
                key={comment.id}
                className={cn(
                  'max-w-[85%] rounded-md px-2.5 py-1.5 text-xs break-words whitespace-pre-wrap',
                  isMine ? 'bg-primary/10 ml-auto' : 'bg-background mr-auto',
                )}
              >
                <div className="text-muted-foreground mb-0.5 text-[10px]">
                  {authorLabel} ·{' '}
                  {new Date(comment.created_at).toLocaleString()}
                </div>
                {comment.new_value}
                {comment.attachments.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {comment.attachments.map((path) => (
                      <AttachmentThumbnail
                        key={path}
                        path={path}
                        signedUrl={signedUrlByPath?.get(path)}
                      />
                    ))}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-muted-foreground text-xs">
          {t('bugReport.thread.empty', {
            defaultValue:
              'No messages yet. Replies from our team will appear here.',
          })}
        </p>
      )}

      {canReply ? (
        <div className="space-y-1.5">
          <Textarea
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            placeholder={t('bugReport.thread.placeholder', {
              defaultValue: 'Write a reply…',
            })}
            className="min-h-14 text-xs"
            maxLength={5_000}
            disabled={isSending}
          />

          {files.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {files.map((file, index) => (
                <span
                  key={`${file.name}-${index}`}
                  className="bg-muted inline-flex items-center gap-1 rounded px-2 py-0.5 text-[10px]"
                >
                  <span className="max-w-[140px] truncate">{file.name}</span>
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-destructive"
                    onClick={() =>
                      setFiles((prev) => prev.filter((_, i) => i !== index))
                    }
                    disabled={isSending}
                    aria-label={t('bugReport.thread.removeAttachment', {
                      defaultValue: 'Remove attachment',
                    })}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept={REPLY_ACCEPTED_MIME.join(',')}
            multiple
            className="hidden"
            onChange={(event) => addFiles(event.target.files)}
          />

          <div className="flex items-center justify-between">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={isSending || files.length >= REPLY_MAX_FILES}
            >
              <Paperclip className="mr-1 h-3.5 w-3.5" />
              {t('bugReport.thread.attach', { defaultValue: 'Attach' })}
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={onSend}
              disabled={isSending || (!message.trim() && files.length === 0)}
            >
              {isSending ? (
                <>
                  <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                  {t('bugReport.thread.sending', { defaultValue: 'Sending…' })}
                </>
              ) : (
                t('bugReport.thread.send', { defaultValue: 'Send reply' })
              )}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
